import {
  useSaveSettings,
  useGetSettings,
  type Settings,
} from '@ais-ui/websockets';
import { useCallback } from 'react';
import {
  useSeedServiceCache,
  useUserSettingsPatch,
  useUserSettingsQuery,
} from './client';
import { useImportStatus } from './importStatus';
import { bestEffort } from './resilience';
import { settingsPhase } from './settingsPhase';
import type { DomainTranslator, SettingsDomain } from './types';

/**
 * Pure resolution of which source the UI should read from. The blob-derived
 * value is ALWAYS the guaranteed fallback; the service is preferred only once we
 * are eligible to trust it (dual-run and the app-level import has completed, or
 * service-only phase 3) AND it has actually returned data. Anything else —
 * loading, timeout, error, empty — transparently falls back to the blob so the
 * UI never breaks.
 */
export function resolveReadSource<T>(args: {
  phase: number;
  imported: boolean;
  blobData: T | undefined;
  serviceData: T | undefined;
}): { data: T | undefined; source: 'blob' | 'service' } {
  const preferService = args.phase >= 3 || (args.phase >= 2 && args.imported);
  if (preferService && args.serviceData !== undefined) {
    return { data: args.serviceData, source: 'service' };
  }
  return { data: args.blobData, source: 'blob' };
}

/**
 * Given the blob AFTER this domain's own `patch` has been translated onto it,
 * returns additional raw blob fields to merge in — for fields this domain
 * doesn't (yet) own but that the same form submit still needs to persist
 * (e.g. a still-blob-only UI toggle living alongside a core-owned field).
 * Receiving the post-translation blob lets the caller layer safely on top of
 * what was just computed (e.g. spread the same `ribbonPlugin.settings`)
 * instead of a stale pre-submit snapshot.
 */
export type ExtraBlobPatch = (translatedBlob: Settings) => Partial<Settings>;

export interface DomainSettingsResult<T> {
  data: T | undefined;
  isLoading: boolean;
  /**
   * Persists `patch` (translated into this domain's owned blob/service
   * fields) and, optionally, `extraBlobPatch` — additional fields this domain
   * doesn't own. Both are merged into the SAME blob save, so one call is
   * always exactly one blob round trip, in every phase — never a second,
   * non-atomic write.
   */
  update: (
    patch: Partial<T>,
    extraBlobPatch?: ExtraBlobPatch,
  ) => Promise<Settings | undefined>;
  source: 'blob' | 'service';
}

export function useDomainSettings<T>(
  domain: SettingsDomain,
  translator: DomainTranslator<T>,
): DomainSettingsResult<T> {
  const phase = settingsPhase();
  const usesService = phase >= 2;

  const { data: blob } = useGetSettings();
  const { mutateAsync: saveBlob } = useSaveSettings();
  const { mutateAsync: patchService } = useUserSettingsPatch<T>(domain);
  const seedServiceCache = useSeedServiceCache();

  // Seeding is done once at login by the import orchestrator; the app-level
  // `imported` flag is the single "trust the service" signal for every domain.
  const { imported } = useImportStatus();
  const serviceEnabled = usesService && (phase >= 3 || imported);
  const serviceQuery = useUserSettingsQuery<T>(domain, {
    enabled: serviceEnabled,
  });

  const update = useCallback(
    async (
      patch: Partial<T>,
      extraBlobPatch?: ExtraBlobPatch,
    ): Promise<Settings | undefined> => {
      const translated = blob ? translator.applyToBlob(blob, patch) : undefined;
      // Merge the caller's extra (non-owned) fields into the SAME blob object
      // this domain's patch produced, so they reach the blob in one save.
      const nextBlob =
        translated && extraBlobPatch
          ? { ...translated, ...extraBlobPatch(translated) }
          : translated;

      if (phase === 1) {
        if (nextBlob) await saveBlob(nextBlob);
        return nextBlob;
      }

      if (phase === 2) {
        // Blob write is authoritative: awaiting it is what makes the user's save
        // succeed and keeps the fallback fresh. The service PATCH is best-effort
        // so a slow/absent test backend never blocks or fails the save.
        if (nextBlob) await saveBlob(nextBlob);
        // Reads prefer the service once imported; seed its cache from the
        // authoritative blob so the just-saved value shows even if the
        // best-effort PATCH below is dropped.
        if (nextBlob) seedServiceCache(domain, translator.fromBlob(nextBlob));
        void bestEffort(patchService(patch), (error) =>
          console.error(
            `Best-effort PATCH to user-settings service for "${domain}" failed:`,
            error,
          ),
        );
        return nextBlob;
      }

      // Phase 3: the service is the sole source of truth for THIS domain, so
      // `translated` (which carries its fields) must never be pushed back into
      // the blob. `extraBlobPatch` fields are still blob-only — the service has
      // never owned them — so they're merged onto the CURRENT blob and saved on
      // their own, never onto `translated`.
      await patchService(patch);
      if (extraBlobPatch && blob)
        await saveBlob({ ...blob, ...extraBlobPatch(blob) });
      return blob;
    },
    [phase, blob, saveBlob, patchService, translator, domain, seedServiceCache],
  );

  const blobData = blob ? translator.fromBlob(blob) : undefined;
  const { data, source } = resolveReadSource<T>({
    phase,
    imported,
    blobData,
    serviceData: serviceEnabled ? serviceQuery.data : undefined,
  });

  // The blob is a guaranteed fallback, so we're only ever "loading" while it is
  // still absent (and, when no blob exists, while the service is fetching).
  const isLoading = blob
    ? false
    : serviceEnabled
      ? serviceQuery.isLoading
      : true;

  return { data, isLoading, update, source };
}
